/* rb-m-core.js — Rooster mobile pass, shared runtime.
   2026-09-11.

   Exposes window.RBM for the other rb-m modules. Everything is a no-op above
   767px so desktop is untouched; the modules also re-check on resize because
   an iPad rotating crosses the breakpoint.

   Deliberately dependency-free and defer-loaded. It must not assume jQuery,
   which the theme loads but which is not guaranteed to have parsed first. */
(function () {
  'use strict';

  var MQ = window.matchMedia('(max-width: 767px)');
  var RBM = window.RBM = {
    mq: MQ,
    isMobile: function () { return MQ.matches; },

    /* Run fn now (if mobile) and again whenever we cross into mobile.
       Guarded so a module never initialises twice. */
    onMobile: function (key, fn) {
      var done = false;
      RBM._mods[key] = fn;
      var run = function () {
        if (!MQ.matches || done) return;
        done = true;
        try { fn(); } catch (e) { console.warn('[rb-m] ' + key + ' failed:', e); }
      };
      run();
      if (MQ.addEventListener) MQ.addEventListener('change', run);
      else if (MQ.addListener) MQ.addListener(run);
      document.addEventListener('DOMContentLoaded', run);
    },
    /* Re-run registered modules by key. The theme's collection filter
       REPLACES .collection-main on every change (bar, drawer, grid, pager),
       so anything built on those nodes has to be rebuilt; the modules are
       idempotent per element (dataset flags), so running them again on the
       new nodes is safe. (23 Sep -- after a filter change the pinned bar,
       count, quick chips, Size-first and Load more were all gone.) */
    _mods: {},
    rerun: function (keys) {
      if (!MQ.matches) return;
      keys.forEach(function (k) {
        var fn = RBM._mods[k]; if (!fn) return;
        try { fn(); } catch (e) { console.warn('[rb-m] rerun ' + k + ' failed:', e); }
      });
    },

    /* DESKTOP (23 Sep). Mark asked for the mobile filter/denim/card work on
       desktop too, so the same primitives exist for >= 768px. Modules live in
       rb-d-*.js and are no-ops on phones; the mobile modules stay no-ops on
       desktop. Nothing here changes what the mobile modules do. */
    isDesktop: function () { return !MQ.matches; },
    onDesktop: function (key, fn) {
      var done = false;
      RBM._dmods[key] = fn;
      var run = function () {
        if (MQ.matches || done) return;
        done = true;
        try { fn(); } catch (e) { console.warn('[rb-d] ' + key + ' failed:', e); }
      };
      run();
      if (MQ.addEventListener) MQ.addEventListener('change', run);
      else if (MQ.addListener) MQ.addListener(run);
      document.addEventListener('DOMContentLoaded', run);
    },
    _dmods: {},
    rerunDesktop: function (keys) {
      if (MQ.matches) return;
      keys.forEach(function (k) {
        var fn = RBM._dmods[k]; if (!fn) return;
        try { fn(); } catch (e) { console.warn('[rb-d] rerun ' + k + ' failed:', e); }
      });
    },

    /* Wait for an element the theme renders late (app blocks, AJAX sections).
       Resolves with null rather than hanging forever. */
    when: function (selector, timeout) {
      return new Promise(function (resolve) {
        var found = document.querySelector(selector);
        if (found) return resolve(found);
        var obs = new MutationObserver(function () {
          var el = document.querySelector(selector);
          if (el) { obs.disconnect(); resolve(el); }
        });
        obs.observe(document.documentElement, { childList: true, subtree: true });
        setTimeout(function () { obs.disconnect(); resolve(document.querySelector(selector)); }, timeout || 6000);
      });
    },

    /* One shared debounce so each module is not shipping its own. */
    debounce: function (fn, wait) {
      var t = 0;
      return function () {
        var args = arguments, self = this;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(self, args); }, wait || 100);
      };
    },

    /* The theme re-renders the collection section over AJAX and fires no
       event. rb-b-swap.js solves this for desktop; this is the mobile twin.
       Calls fn when the subtree really changed, not when a lazy image
       swapped its src — that fired 9 times in one ordinary scroll and cost
       ~295ms of forced layout on the desktop build. */
    onSectionSwap: function (root, fn) {
      if (!root) return;
      var pending = 0;
      var obs = new MutationObserver(function (records) {
        var real = false;
        for (var i = 0; i < records.length; i++) {
          var r = records[i];
          if (r.type !== 'childList') continue;
          for (var j = 0; j < r.addedNodes.length; j++) {
            var n = r.addedNodes[j];
            if (n.nodeType !== 1) continue;
            if (n.tagName === 'IMG' || n.tagName === 'SCRIPT' || n.tagName === 'STYLE') continue;
            /* 23 Sep: our own paints (rbm- / rbd- classed nodes, e.g. the "+n"
               swatch count) and Option B's hover tray redraws are not a re-render.
               Without this, paintSwatches -> mutation -> paintSwatches looped
               every 60ms, and a hover over a card wiped the "+n" it opened. */
            var cls = String(n.className || '');
            if (/(^|\s)rb[md]-/.test(cls)) continue;
            if (n.closest && n.closest('.js-quick-add-overlay, .rb-qas')) continue;
            real = true; break;
          }
          if (real) break;
        }
        if (!real) return;
        clearTimeout(pending);
        pending = setTimeout(fn, 60);
      });
      obs.observe(root, { childList: true, subtree: true });
    }
  };

  /* ---- promo bar collapse -------------------------------------------------
     The promo/free-shipping bar is rendered by the theme somewhere above
     header#top and its class name is not stable across the announcement
     sections in this theme (there are two, at 45KB and 44KB). Rather than
     guess a selector, find the last visible element before the sticky header
     inside .page-container and tag it. If nothing matches we simply do
     nothing — the header alone is already only 54px. */
  RBM.onMobile('chrome', function () {
    var header = document.querySelector('header#top, .js-header');
    if (!header) return;

    var promo = null;
    var prev = header.previousElementSibling;
    while (prev && !promo) {
      if (prev.nodeType === 1 && !/^(SCRIPT|STYLE|NOSCRIPT|LINK)$/.test(prev.tagName)) {
        var r = prev.getBoundingClientRect();
        if (r.height > 8 && r.height < 120) promo = prev;
      }
      prev = prev.previousElementSibling;
    }
    /* The header may be wrapped in its own section div; look one level up. */
    if (!promo) {
      var group = header.closest('.shopify-section') || header.parentElement;
      var gprev = group && group.previousElementSibling;
      while (gprev && !promo) {
        if (gprev.nodeType === 1 && !/^(SCRIPT|STYLE|NOSCRIPT|LINK)$/.test(gprev.tagName)) {
          var r2 = gprev.getBoundingClientRect();
          if (r2.height > 8 && r2.height < 120) promo = gprev;
        }
        gprev = gprev.previousElementSibling;
      }
    }

    var setChrome = function () {
      var h = header.getBoundingClientRect().height || 54;
      document.documentElement.style.setProperty('--rbm-chrome', Math.round(h) + 'px');
    };
    setChrome();
    window.addEventListener('resize', RBM.debounce(setChrome, 120), { passive: true });

    if (promo) {
      promo.classList.add('rbm-promo-collapsible');
      /* Lock in the natural height so the transition has something to go to
         and coming back from collapsed does not jump. */
      var natural = Math.round(promo.getBoundingClientRect().height);
      promo.style.height = natural + 'px';
      window.addEventListener('resize', RBM.debounce(function () {
        if (document.documentElement.classList.contains('rbm-scrolled')) return;
        promo.style.height = '';
        natural = Math.round(promo.getBoundingClientRect().height);
        promo.style.height = natural + 'px';
      }, 150), { passive: true });
    }

    var ticking = false;
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.pageYOffset || document.documentElement.scrollTop;
        /* 40 down / 10 up so a rubber-band scroll does not flicker it. */
        var on = document.documentElement.classList.contains('rbm-scrolled');
        if (!on && y > 40) document.documentElement.classList.add('rbm-scrolled');
        else if (on && y < 10) document.documentElement.classList.remove('rbm-scrolled');
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  });

  /* ---- sheet plumbing -----------------------------------------------------
     One scrim, one open sheet at a time, Escape and scrim-tap close, body
     scroll locked while open. Used by the filter sheet and the size sheet. */
  var openSheet = null, scrim = null;

  RBM.closeSheet = function () {
    if (!openSheet) return;
    openSheet.classList.remove('is-open');
    if (scrim) scrim.classList.remove('is-open');
    document.documentElement.classList.remove('rbm-sheet-open');
    var s = openSheet;
    openSheet = null;
    setTimeout(function () {
      if (scrim && !openSheet) scrim.remove(), scrim = null;
      if (s && s.dataset.rbmTransient === '1') s.remove();
    }, 300);
  };

  RBM.openSheet = function (el) {
    if (!el) return;
    if (openSheet && openSheet !== el) RBM.closeSheet();
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.className = 'rbm-scrim';
      scrim.addEventListener('click', RBM.closeSheet);
      document.body.appendChild(scrim);
    }
    openSheet = el;
    document.documentElement.classList.add('rbm-sheet-open');
    /* double rAF so the transform transition actually runs from translateY(100%) */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('is-open');
        if (scrim) scrim.classList.add('is-open');
      });
    });
  };

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openSheet) RBM.closeSheet();
  });

  /* Swipe the sheet down to dismiss. */
  document.addEventListener('touchstart', function (e) {
    if (!openSheet) return;
    var grab = e.target.closest && e.target.closest('.rbm-sheet__grab, .rbm-sheet__head');
    if (!grab) return;
    var startY = e.touches[0].clientY, moved = 0;
    var move = function (ev) {
      moved = ev.touches[0].clientY - startY;
      if (moved > 0) openSheet.style.transform = 'translateY(' + moved + 'px)';
    };
    var end = function () {
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
      if (!openSheet) return;
      openSheet.style.transform = '';
      if (moved > 90) RBM.closeSheet();
    };
    document.addEventListener('touchmove', move, { passive: true });
    document.addEventListener('touchend', end);
  }, { passive: true });
})();
